import { faPlus } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Checkbox, ListCard, Popup, Selectbox, Textbox } from "@streetsheaver/sh-ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useState } from "react";
import { useParams } from "react-router";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import NoEnvSelected from "../../components/NoEnvSelected/NoEnvSelected";
import { EnvironmentContext } from "../../context/EnvironmentContextProvider";
import { callAPI } from "../../helpers/callAPI";
import { useTenants } from "../../hooks/useTenants";
import "./ReportImages.scss";

const SIZE_OPTIONS = [
	{ label: "Small", value: "Small" },
	{ label: "Medium", value: "Medium" },
	{ label: "Large", value: "Large" },
];

const fileToBase64 = (file) => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => {
			const base64 = reader.result.split(",")[1];
			resolve(base64);
		};
		reader.onerror = (error) => reject(error);
	});
};

export default function ReportImages() {
	const { selectedEnv } = useContext(EnvironmentContext);
	const tenantName = useParams()?.tenantName;
	const { tenants, tenantsLoading } = useTenants();
	const tenantId = tenants?.find((t) => t.name === tenantName)?.reportGeneratorTenant;
	const queryClient = useQueryClient();
	const [showAddPopup, setShowAddPopup] = useState(false);
	const [newLogoForm, setNewLogoForm] = useState({
		name: "",
		filename: "",
		size: "Medium",
		logo: null,
		isDefault: false,
	});
	const [selectedFile, setSelectedFile] = useState(null);
	const [formErrors, setFormErrors] = useState([]);
	const [deleteConfirmation, setDeleteConfirmation] = useState({ visible: false, image: null });

	const { data: reportImages, isLoading: reportImagesLoading } = useQuery({
		queryKey: ["ReportImages", selectedEnv, tenantId],
		queryFn: async () => {
			const res = await callAPI({ url: `tenants/${tenantId}/images/logos`, selectedEnv: selectedEnv });
			return res.data;
		},
		refetchOnWindowFocus: false,
		enabled: !!selectedEnv && !!tenantId && !tenantsLoading,
	});

	const addLogo = useMutation({
		mutationFn: async () => {
			const errors = [];
			if (!newLogoForm.name.trim()) errors.push("name");
			if (!selectedFile) errors.push("file");
			setFormErrors(errors);
			if (errors.length > 0) return;

			const base64Logo = await fileToBase64(selectedFile);
			const body = {
				tenantId: tenantId,
				name: newLogoForm.name,
				filename: selectedFile.name,
				size: newLogoForm.size,
				logo: base64Logo,
				isDefault: newLogoForm.isDefault,
			};

			return await callAPI({
				url: `tenants/${tenantId}/images/logos`,
				method: "post",
				body: body,
				selectedEnv: selectedEnv,
			});
		},
		onSuccess: (res) => {
			if (res) {
				queryClient.invalidateQueries(["ReportImages", selectedEnv, tenantId]);
				handleClosePopup();
			}
		},
	});

	const deleteLogo = useMutation({
		mutationFn: async (imageId) => {
			return await callAPI({
				url: `tenants/${tenantId}/images/logos/${imageId}`,
				method: "delete",
				selectedEnv: selectedEnv,
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries(["ReportImages", selectedEnv, tenantId]);
		},
	});

	const setAsDefault = useMutation({
		mutationFn: async (image) => {
			const body = {
				tenantId: tenantId,
				name: image.name,
				filename: image.filename,
				size: image.size,
				isDefault: true,
			};
			return await callAPI({
				url: `tenants/${tenantId}/images/logos/${image.id}`,
				method: "put",
				body: body,
				selectedEnv: selectedEnv,
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries(["ReportImages", selectedEnv, tenantId]);
		},
	});

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setSelectedFile(file);
			setNewLogoForm((prev) => ({ ...prev, filename: file.name }));
		}
	};

	const handleClosePopup = () => {
		setShowAddPopup(false);
		setNewLogoForm({
			name: "",
			filename: "",
			size: "Medium",
			logo: null,
			isDefault: false,
		});
		setSelectedFile(null);
		setFormErrors([]);
	};

	if (!selectedEnv) return <NoEnvSelected />;
	if (reportImagesLoading || tenantsLoading) return <LoadingSpinner />;

	const AddLogoContent = (
		<div className="AddLogo">
			<h2>Add New Logo</h2>
			<Textbox
				title="Display Name"
				value={newLogoForm.name}
				onChange={(e) => setNewLogoForm((prev) => ({ ...prev, name: e.target.value }))}
				isError={formErrors.includes("name")}
				errorMessage="Display name is required"
			/>
			<div className="AddLogo__fileInput">
				<label>Image File</label>
				<input
					type="file"
					accept=".svg,.jpeg,.jpg,.png,.webp,image/svg+xml,image/jpeg,image/png,image/webp"
					onChange={handleFileChange}
				/>
				<p className="AddLogo__fileInput__hint">
					Supported formats: SVG (preferred), JPEG, PNG, WebP. SVG is recommended as it scales without loss of quality.
				</p>
				{selectedFile && <p className="AddLogo__fileInput__filename">Selected: {selectedFile.name}</p>}
				{formErrors.includes("file") && (
					<p className="AddLogo__fileInput__error">Please select an image file</p>
				)}
			</div>
			<Selectbox
				title="Size"
				items={SIZE_OPTIONS}
				displayExpr="label"
				defaultSelectedItem={SIZE_OPTIONS.find((o) => o.value === newLogoForm.size)}
				selectedItem={SIZE_OPTIONS.find((o) => o.value === newLogoForm.size)}
				onChange={(e) => setNewLogoForm((prev) => ({ ...prev, size: e?.value || "Medium" }))}
			/>
			<Checkbox
				label="Set as default logo"
				checked={newLogoForm.isDefault}
				onChange={(e) => setNewLogoForm((prev) => ({ ...prev, isDefault: e.target.checked }))}
			/>
			<Button text="Add Logo" type="CTA" unpadded onClick={() => addLogo.mutate()} />
		</div>
	);

	return (
		<div className="ReportImages">
			<div className="ReportImages__header">
				<Button
					text="Add Logo"
					type="CTA"
					unpadded
					icon={<FontAwesomeIcon icon={faPlus} />}
					onClick={() => setShowAddPopup(true)}
				/>
			</div>
			<div className="ReportImages__cards">
				{reportImages?.length > 0 ? (
					reportImages.map((image) => (
						<ListCard
							key={image.id}
							title={
								<div className="ReportImages__cards__title">
									<span>{image.name}</span>
									{image.isDefault && <span className="ReportImages__cards__title__default">Default</span>}
								</div>
							}
							subtext1={`Filename: ${image.filename} • Size: ${image.size}`}
							subtext2={`ID: ${image.id}`}
							button1={
								!image.isDefault
									? {
											label: "Set as Default",
											onClick: () => setAsDefault.mutate(image),
									  }
									: undefined
							}
							button2={
								!image.isDefault || reportImages.length === 1
									? {
											label: "Delete",
											onClick: () => setDeleteConfirmation({ visible: true, image }),
									  }
									: undefined
							}
						/>
					))
				) : (
					<div className="ReportImages__empty">
						<p>No report images found for this tenant.</p>
						<Button
							text="Add Your First Logo"
							type="CTA"
							unpadded
							icon={<FontAwesomeIcon icon={faPlus} />}
							onClick={() => setShowAddPopup(true)}
						/>
					</div>
				)}
			</div>
			<Popup
				renderContent={() => AddLogoContent}
				noHeader
				noPadding
				visible={showAddPopup}
				onOutsideClick={handleClosePopup}
				onCloseButtonClick={handleClosePopup}
			/>
			<Popup
				renderContent={() => (
					<div className="DeleteConfirmation">
						<h2>Delete Logo</h2>
						<p>Are you sure you want to delete "{deleteConfirmation.image?.name}"?</p>
						<div className="DeleteConfirmation__buttons">
							<Button
								text="Cancel"
								unpadded
								onClick={() => setDeleteConfirmation({ visible: false, image: null })}
							/>
							<Button
								text="Delete"
								type="CTA"
								unpadded
								onClick={() => {
									deleteLogo.mutate(deleteConfirmation.image?.id);
									setDeleteConfirmation({ visible: false, image: null });
								}}
							/>
						</div>
					</div>
				)}
				noHeader
				noPadding
				visible={deleteConfirmation.visible}
				onOutsideClick={() => setDeleteConfirmation({ visible: false, image: null })}
				onCloseButtonClick={() => setDeleteConfirmation({ visible: false, image: null })}
			/>
		</div>
	);
}
